import { format, parseISO } from 'date-fns';
import { router } from 'expo-router';
import { useCallback, useMemo } from 'react';
import {
  ScrollViewProps,
  SectionList,
  SectionListData,
  View,
  useWindowDimensions,
} from 'react-native';
import { StyleSheet } from 'react-native';

import { RewardsPointsProgress } from '@fhs/loyalty/src/components';
import {
  ActionSheet,
  ActionSheetScreenHeader,
  ErrorCard,
  IconReceiptLongOutline,
  MediaQueriesContext,
  Skeleton,
  Text,
  mobileMediaQueries,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { ITransactionFragment } from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useLoyaltyUserTransactions } from '../../queries/loyalty.queries';

import { TransactionItem } from './transaction-item';
import { TransactionItemSkeleton } from './transaction-item-skeleton';

type TransactionSection =
  | {
      isLoading: false;
      title: string;
      data: ITransactionFragment[];
    }
  | {
      isLoading: true;
      title: string;
      data: any[];
    };
const createSections = (transactions: ITransactionFragment[] | undefined): TransactionSection[] => {
  if (!transactions) {
    return [];
  }

  // Group transactions by month
  const groupedTransactions =
    transactions?.reduce(
      (groups: { [key: string]: TransactionSection }, transaction: ITransactionFragment) => {
        const date = parseISO(transaction.createdAt);
        const monthKey = format(date, 'yyyy-MM');
        const monthTitle = format(date, 'MMMM');

        if (!groups[monthKey]) {
          groups[monthKey] = {
            isLoading: false,
            title: monthTitle,
            data: [],
          };
        }

        groups[monthKey].data.push(transaction);
        return groups;
      },
      {}
    ) ?? {};

  const sections = Object.values(groupedTransactions) as TransactionSection[];
  sections.sort((a, b) => {
    const dateA = parseISO(a.data[0].createdAt);
    const dateB = parseISO(b.data[0].createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  return sections;
};

const renderItem = ({
  item,
  section,
}: {
  item: ITransactionFragment;
  section: SectionListData<any, TransactionSection>;
}) => {
  if (section.isLoading) {
    return <TransactionItemSkeleton />;
  }
  return <TransactionItem transaction={item} />;
};

const renderSectionHeader = ({
  section: { title, isLoading },
}: {
  section: SectionListData<ITransactionFragment, TransactionSection>;
}) => {
  if (isLoading) {
    return <Skeleton style={styles.sectionHeaderSkeleton} />;
  }
  return (
    <View style={styles.sectionHeader}>
      <Text.Heading type="three">{title}</Text.Heading>
    </View>
  );
};

const renderHeader = () => {
  return <RewardsPointsProgress style={styles.pointsProgress} />;
};

const renderSeparator = () => {
  return <View style={styles.separator} />;
};

const renderSectionFooter = () => {
  return <View style={styles.sectionFooter} />;
};

const renderFooter = () => {
  return <View style={styles.footer} />;
};

const loadingSections: TransactionSection[] = [
  {
    isLoading: true,
    title: '',
    data: Array.from({ length: 6 }, () => ({})),
  },
  {
    isLoading: true,
    title: '',
    data: Array.from({ length: 3 }, () => ({})),
  },
  {
    isLoading: true,
    title: '',
    data: Array.from({ length: 3 }, () => ({})),
  },
];

export function RewardsHistoryScreen() {
  const { loyaltyUserReady } = useLoyaltyLegacyStates();
  const {
    data: loyaltyUserTransactions,
    isLoading: loyaltyUserTransactionsLoading,
    refetch,
    isError,
  } = useLoyaltyUserTransactions();
  const isLoading = loyaltyUserTransactionsLoading || !loyaltyUserReady;

  const { height: screenHeight } = useWindowDimensions();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const containerStyle = isDesktop ? { height: screenHeight } : ({ height: '100%' } as const);
  const drawerProps = useMemo(() => ({ style: { flex: 1 } }), []);

  const sections = useMemo(
    () => createSections(loyaltyUserTransactions),
    [loyaltyUserTransactions]
  );

  const onClose = useCallback(() => {
    router.dismiss();
  }, []);

  const renderEmpty = useCallback(() => {
    return (
      <View style={styles.emptyContainer}>
        <IconReceiptLongOutline size={80} />
        <Text.Heading type="two" style={styles.emptyTitle}>
          No Rewards Transactions
        </Text.Heading>
        <Text.Ui size="md" style={styles.emptyDescription}>
          Redeem a reward online or in-restaurant to see it in your transaction history.
        </Text.Ui>
      </View>
    );
  }, []);

  const renderScrollComponent = useCallback((props: ScrollViewProps) => {
    return <ActionSheet.ScrollView {...props} />;
  }, []);

  if (isError) {
    return <ErrorCard buttonHandle={refetch} />;
  }

  return (
    <ActionSheet
      onRequestClose={onClose}
      isVisible
      onClose={onClose}
      containerStyle={containerStyle}
      ignoreBottomSafeArea
      drawerProps={drawerProps}
    >
      <MediaQueriesContext.Provider value={mobileMediaQueries}>
        <ActionSheetScreenHeader title="Rewards History" />
        <SectionList
          style={styles.page}
          contentContainerStyle={styles.contentContainer}
          sections={isLoading ? loadingSections : sections}
          stickySectionHeadersEnabled={false}
          renderItem={renderItem}
          renderSectionHeader={renderSectionHeader}
          renderSectionFooter={renderSectionFooter}
          ListHeaderComponent={renderHeader}
          ItemSeparatorComponent={renderSeparator}
          ListFooterComponent={renderFooter}
          ListEmptyComponent={renderEmpty}
          renderScrollComponent={renderScrollComponent}
        />
      </MediaQueriesContext.Provider>
    </ActionSheet>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: tokens.colors.$white,
    paddingTop: 20,
  },
  contentContainer: {
    flexGrow: 1,
  },
  sectionHeader: {
    paddingHorizontal: 16,
  },
  sectionHeaderSkeleton: {
    marginHorizontal: 16,
    height: 20,
    width: 80,
  },
  separator: {
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  sectionFooter: {
    width: '100%',
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity04,
    marginBottom: 20,
  },
  pointsProgress: {
    paddingHorizontal: 16,
    marginBottom: 30,
  },
  footer: {
    marginBottom: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 100,
  },
  emptyTitle: {
    marginTop: 16,
    textAlign: 'center',
  },
  emptyDescription: {
    marginTop: 8,
    textAlign: 'center',
  },
});
